import React, {FC, useCallback} from 'react';
import {connect} from 'react-redux';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {
  useInsertRecipientOrGroup,
  useRecipientsFetcher,
} from '../../../../../srcCommon/inboxes/recipients/recipientsHooks';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {CompanyModel} from '../../../../models/api/companyModel';
import {ContactSourcesEnum} from '../../../../models/api/contactModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {VisualContactModel} from '../../../../models/api/visualContactModel';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getFindTeammateAndOnlineStatusForVisualContact} from '../../../../store/data/contacts/contactsSelectors';
import {RecipientsDropdown} from '../../dropdowns/autocomplete/recipientsDropdown';

/*
 * Props.
 */

interface RecipientsDropdownContainerProps {
  anchor: Anchor;
  isDropdownVisible: boolean;
  namespace: string;
  source: ContactSourcesEnum;
  role: RecipientRolesEnum;
  query: string;
  onRecipientsSelect: HandlerOf<ReadonlyArray<VisualRecipientModel>>;
  onRequestClose?: Handler;
}

interface RecipientsDropdownStateProps {
  company: CompanyModel;
  findTeammateAndOnlineStatusForVisualContact: (visualContact: VisualContactModel) => VisualContactModel;
}

type RecipientsDropdownContainerComponentProps = RecipientsDropdownContainerProps &
  RecipientsDropdownStateProps;

/*
 * Component.
 */

const RecipientsDropdownContainerComponent: FC<RecipientsDropdownContainerComponentProps> = (props) => {
  const {findTeammateAndOnlineStatusForVisualContact} = props;
  const enrichVisualRecipient = useCallback(
    (vr: VisualRecipientModel) => {
      const contactWithTeammateAndOnlineStatus = findTeammateAndOnlineStatusForVisualContact(vr);
      return {
        ...vr,
        ...contactWithTeammateAndOnlineStatus,
      };
    },
    [findTeammateAndOnlineStatusForVisualContact],
  );
  const {fetcher} = useRecipientsFetcher({
    ...props,
    enrichVisualRecipient,
  });
  const {onRecipientSelect, onGroupSelect} = useInsertRecipientOrGroup(props);

  if (!props.isDropdownVisible) {
    return null;
  }

  return (
    <RecipientsDropdown
      {...props}
      fetcher={fetcher}
      onRecipientSelect={onRecipientSelect}
      onGroupSelect={onGroupSelect}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  company: getCompany,
  findTeammateAndOnlineStatusForVisualContact: getFindTeammateAndOnlineStatusForVisualContact,
}));

export const RecipientsDropdownContainer = connect(mapStateToProps)(RecipientsDropdownContainerComponent);
